import React, { useCallback, useEffect, useState } from "react";
import Modal from "./Modal";
import { api, formatApiError } from "../lib/api";
import { formatoDinero } from "../lib/money";
import {
  FORMAS_PAGO, TEXTOS, fechaCorta, rangoFechaSugerido, cuerpoRegistro, anticipoPorAplicar,
  indicadorAnticipo, etiquetaForma, puedeRegistrarEnCita, ESTADO_CITA_DE_ANTICIPO,
} from "../lib/anticipos";
import { toast } from "sonner";
import { AlertTriangle, Ban, Wallet } from "lucide-react";

// Ventana del anticipo de UNA cita (CL.4 A2): ver lo registrado, registrar uno nuevo y
// anularlo. Contrato en `lib/anticipos.js`.
//
// 🔴 LO QUE NO HACE, PORQUE LO HACE EL BACKEND: no cambia el estado de la cita (registrar
//    la pasa a Confirmado en la misma transacción; anular la devuelve a Pendiente sólo si
//    fue este anticipo quien la confirmó), no calcula el límite ni el estado (llegan
//    derivados) y no valida la fecha contra la zona de la clínica (lo hace el servidor).
//
// 🔴 LA DEMO NO VE NINGÚN BOTÓN (DT.71), y sin la capacidad tampoco: `puedeRegistrar`
//    (cobros) y `puedeAnular` (cobros_anular) los decide el padre con `puede()`. Esconder
//    no es un permiso: el backend rechaza igual.
//
// Errores: `{detail, code}` top-level (DT.60) y SIEMPRE por `formatApiError`, que convierte
// una lista de validación de FastAPI en texto en vez de pintar "[object Object]".
const CAMPO = "w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-brand-electric";


export default function AnticipoModal({
  open, cita, onClose, onCambio, onResolver, puedeRegistrar, puedeAnular, esDemo,
}) {
  const [lista, setLista] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);            // {detail, code}
  const [monto, setMonto] = useState("");
  const [forma, setForma] = useState("");
  const [fecha, setFecha] = useState("");
  const [fechaTocada, setFechaTocada] = useState(false);
  const [referencia, setReferencia] = useState("");
  const [nota, setNota] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [fueraDePlazo, setFueraDePlazo] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [anulando, setAnulando] = useState(false);
  const rango = rangoFechaSugerido();

  const cargar = useCallback(async () => {
    if (!cita?.id) return;
    setCargando(true);
    try {
      const { data } = await api.get("/anticipos", { params: { appointment_id: cita.id } });
      setLista(data);
      // Prellenado con el SUGERIDO del servicio, tal cual llega (cadena). Sin parseFloat.
      setMonto((m) => m || data.anticipo_sugerido || cita.anticipo_sugerido || "");
    } catch (e) {
      setError({ detail: formatApiError(e?.response?.data?.detail), code: e?.response?.data?.code || null });
    } finally {
      setCargando(false);
    }
  }, [cita]);

  useEffect(() => {
    if (!open) return;
    setLista(null); setError(null); setFueraDePlazo(false); setMotivo("");
    setMonto(cita?.anticipo_sugerido || ""); setForma(""); setReferencia(""); setNota("");
    setFecha(rango.hoy); setFechaTocada(false);
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cita?.id]);

  if (!open || !cita) return null;

  const vivo = anticipoPorAplicar(lista);
  const cancelada = cita.status === "Cancelado";
  const pideDecision = !!vivo && (cita.status === "Cancelado" || cita.status === "No asistió");
  // Corrección 1: sólo con la cita Pendiente / Confirmado y sin anticipo vivo (por aplicar
  // o aplicado). En Atendido, No asistió o Cancelado la ventana es de lectura o de desenlace.
  const mostrarRegistro = !esDemo && puedeRegistrar && lista !== null && puedeRegistrarEnCita(cita.status, lista);
  const mostrarAnular = !esDemo && puedeAnular && !!vivo;
  const items = (lista && lista.items) || [];

  const registrar = async () => {
    setError(null);
    const r = cuerpoRegistro({
      appointmentId: cita.id, monto, forma, fecha, fechaTocada, referencia, nota,
    });
    if (r.error) { setError({ detail: TEXTOS[r.error], code: "VALIDACION_LOCAL" }); return; }
    setGuardando(true);
    try {
      const { data } = await api.post("/anticipos", r.cuerpo);
      if (data?.fuera_de_plazo) {
        setFueraDePlazo(true);
        toast.warning(TEXTOS.fuera_de_plazo);
      } else {
        toast.success(TEXTOS.registrado_ok);
      }
      await cargar();
      onCambio?.();   // la Agenda relee: la cita pasó a Confirmado en el backend
    } catch (e) {
      setError({ detail: formatApiError(e?.response?.data?.detail), code: e?.response?.data?.code || null });
    } finally {
      setGuardando(false);
    }
  };

  const anular = async () => {
    if (!vivo || !motivo.trim()) return;
    setAnulando(true); setError(null);
    try {
      const { data } = await api.post(`/anticipos/${vivo.id}/anular`, { motivo: motivo.trim() });
      toast.success(data?.cita_revertida ? TEXTOS.anulado_cita_revertida : TEXTOS.anulado_ok);
      setMotivo("");
      await cargar();
      onCambio?.();
    } catch (e) {
      setError({ detail: formatApiError(e?.response?.data?.detail), code: e?.response?.data?.code || null });
    } finally {
      setAnulando(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={guardando || anulando ? undefined : onClose}
      title={TEXTOS.titulo_modal}
      size="lg"
      testid="modal-anticipo"
      footer={
        <button type="button" onClick={onClose} disabled={guardando || anulando}
                className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                data-testid="anticipo-cerrar">
          {TEXTOS.cerrar}
        </button>
      }
    >
      <div className="text-sm text-slate-600 mb-4 pb-3 border-b border-slate-200">
        <span className="font-medium text-slate-900">{cita.patient_name}</span>
        {" · "}{cita.service_name || "Sin servicio"}{" · "}{cita.date} {cita.time}
        {cita.doctor_name ? ` · ${cita.doctor_name}` : ""}
      </div>

      {lista && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4 text-sm">
          <div className="rounded-xl border border-slate-200 px-3 py-2">
            <div className="text-xs text-slate-500">{TEXTOS.limite}</div>
            <div className="font-medium" data-testid="anticipo-limite">{fechaCorta(lista.anticipo_limite)}</div>
          </div>
          {lista.anticipo_sugerido ? (
            <div className="rounded-xl border border-slate-200 px-3 py-2">
              <div className="text-xs text-slate-500">{TEXTOS.sugerido}</div>
              <div className="font-medium" data-testid="anticipo-sugerido">{formatoDinero(lista.anticipo_sugerido)}</div>
            </div>
          ) : null}
        </div>
      )}

      {fueraDePlazo && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800"
             data-testid="anticipo-fuera-de-plazo">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{TEXTOS.fuera_de_plazo}</span>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
             data-testid="anticipo-error">
          {error.detail}
        </div>
      )}

      {cargando && !lista ? (
        <div className="py-8 text-center text-sm text-slate-500">{TEXTOS.cargando}</div>
      ) : lista ? (
        <div className="space-y-4">
          {items.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="anticipo-sin-registros">{TEXTOS.sin_registros}</p>
          ) : (
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{TEXTOS.registrados}</div>
              <ul className="space-y-1.5" data-testid="anticipo-lista">
                {items.map((a) => {
                  const anulado = a.estado === "anulado";
                  const ind = anulado ? null : indicadorAnticipo(ESTADO_CITA_DE_ANTICIPO[a.estado]);
                  return (
                    <li key={a.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-slate-50 text-sm"
                        data-testid={`anticipo-item-${a.id}`}>
                      <span className={anulado ? "line-through text-slate-400" : ""}>
                        <span className="font-medium">{formatoDinero(a.monto)}</span>
                        {" · "}{TEXTOS.recibido_el} {fechaCorta(a.fecha_recibido)}
                        {" · "}{etiquetaForma(a.forma_pago)}
                        {a.referencia ? ` · ${a.referencia}` : ""}
                        {a.resolucion === "devuelto" && a.devolucion_fecha
                          ? ` · ${TEXTOS.devuelto_el} ${fechaCorta(a.devolucion_fecha)}` : ""}
                        {anulado && <span className="ml-2 text-xs text-red-600">{TEXTOS.anulado_marca} {a.anulado_motivo}</span>}
                      </span>
                      {ind && (
                        <span className={`text-[10px] font-medium border rounded-full px-1.5 py-0.5 shrink-0 ${ind.clase}`}>
                          {ind.etiqueta}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {pideDecision && !esDemo && puedeAnular && onResolver && (
            <button type="button" onClick={() => onResolver(cita)}
                    className="w-full py-2 text-sm rounded-lg border border-brand-electric text-brand-electric hover:bg-blue-50"
                    data-testid="anticipo-resolver">
              {TEXTOS.resolver_boton}
            </button>
          )}

          {cancelada && !vivo && !esDemo && puedeRegistrar && (
            <p className="text-sm text-slate-500" data-testid="anticipo-cancelada">{TEXTOS.cancelada_sin_anticipo}</p>
          )}

          {mostrarRegistro && (
            <div className="border-t border-slate-200 pt-3 space-y-2" data-testid="anticipo-form">
              <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                <Wallet className="w-4 h-4" /> {TEXTOS.registrar_titulo}
              </div>
              <p className="text-xs text-slate-500" data-testid="anticipo-ayuda">
                {TEXTOS.registrar_ayuda}
                {/* El backend confirma la cita SÓLO desde Pendiente: la frase sólo se pinta ahí. */}
                {cita.status === "Pendiente" ? ` ${TEXTOS.registrar_confirma}` : ""}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-slate-500">{TEXTOS.campo_monto}</label>
                  <input className={CAMPO} inputMode="decimal" placeholder="0.00" value={monto}
                         onChange={(e) => setMonto(e.target.value)} data-testid="anticipo-monto" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">{TEXTOS.campo_forma}</label>
                  <select className={CAMPO} value={forma} onChange={(e) => setForma(e.target.value)}
                          data-testid="anticipo-forma">
                    <option value="">{TEXTOS.campo_forma_elige}</option>
                    {FORMAS_PAGO.map((f) => <option key={f.valor} value={f.valor}>{f.etiqueta}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">{TEXTOS.campo_fecha}</label>
                  <input type="date" className={CAMPO} value={fecha} max={rango.hoy} min={rango.min}
                         onChange={(e) => { setFecha(e.target.value); setFechaTocada(true); }}
                         data-testid="anticipo-fecha" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500">{TEXTOS.campo_referencia}</label>
                  <input className={CAMPO} value={referencia} placeholder={TEXTOS.campo_referencia_ph}
                         onChange={(e) => setReferencia(e.target.value)} data-testid="anticipo-referencia" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">{TEXTOS.campo_nota}</label>
                  <input className={CAMPO} value={nota} onChange={(e) => setNota(e.target.value)}
                         data-testid="anticipo-nota" />
                </div>
              </div>
              <div className="flex justify-end">
                <button type="button" onClick={registrar} disabled={guardando}
                        className="px-4 py-2 text-sm rounded-lg bg-brand-electric text-white hover:bg-blue-700 disabled:opacity-60 inline-flex items-center gap-1.5"
                        data-testid="anticipo-registrar">
                  <Wallet className="w-4 h-4" /> {guardando ? TEXTOS.registrando : TEXTOS.boton_registrar}
                </button>
              </div>
            </div>
          )}

          {mostrarAnular && (
            <div className="flex items-end gap-2 border-t border-slate-200 pt-3">
              <div className="flex-1">
                <label className="text-xs text-slate-500">{TEXTOS.anular_campo}</label>
                <input className={CAMPO} placeholder={TEXTOS.anular_ph} value={motivo}
                       onChange={(e) => setMotivo(e.target.value)} data-testid="anticipo-motivo" />
              </div>
              <button type="button" onClick={anular} disabled={anulando || !motivo.trim()}
                      className="px-3 py-2 text-sm rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-60 inline-flex items-center gap-1.5"
                      data-testid="anticipo-anular">
                <Ban className="w-4 h-4" /> {anulando ? TEXTOS.anulando : TEXTOS.anular_boton}
              </button>
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
